import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../../../styles/ui.css';
import './ChildRequestPage.css';

const API = process.env.REACT_APP_API_BASE_URL;
const API_ORIGIN = API?.replace(/\/api\/?$/, '') || '';

const LABEL = {
  submitted: 'Submitted',
  resubmitted: 'Resubmitted',
  accepted: 'Accepted',
  resubmit: 'Request Resubmit',
  clarification: 'Clarification Needed',
};

function childStatusClass(status) {
  if (status === 'Approved') return 'child-status approved';
  if (status === 'Rejected' || status === 'Request Resubmit') return 'child-status rejected';
  return 'child-status submitted';
}

function docStatusClass(s) {
  if (s === 'accepted') return 'document-status accepted';
  if (s === 'resubmitted') return 'document-status resubmitted';
  if (s === 'submitted' || s === 'clarification') return 'document-status pending';
  return 'document-status none';
}

export default function ChildRequestPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const roleId = Number(localStorage.getItem('role_id') || 1);
  const token = localStorage.getItem('token');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [overallComment, setOverallComment] = useState('');
  const [perDoc, setPerDoc] = useState({});

  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }),
    [token]
  );

  async function load() {
    if (
      !childId ||
      !/^[0-9a-f-]{8}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{12}$/i.test(childId)
    ) {
      navigate('/my-requests');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/children/${childId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      setData(j);

      const init = {};
      (j.documents || []).forEach((d) => {
        init[d.document_id] = {
          status: d.status === 'submitted' ? 'accepted' : d.status,
          comment: '',
        };
      });
      setPerDoc(init);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [childId]);

  const isReviewer = roleId === 3 && data?.reviewerState?.assigned;
  const reviewerCanEdit = !!data?.reviewerState?.canEdit;
  const reviewerFinalized = !!data?.reviewerState?.finalized;

  const isSecretary = roleId === 4;
  const childStatus = data?.child?.status;
  const canApprove = isSecretary && childStatus !== 'Approved';
  const canSendBack = isSecretary && childStatus !== 'Approved';

  function onChangeDoc(documentId, field, value) {
    setPerDoc((prev) => ({ ...prev, [documentId]: { ...prev[documentId], [field]: value } }));
  }

  async function saveReview() {
    setSaving(true);
    try {
      const documentFeedback = Object.entries(perDoc)
        .map(([documentId, v]) => ({
          documentId,
          status: v.status,
          comment: v.comment || null,
        }))
        .filter((x) => ['accepted', 'resubmit', 'clarification'].includes(x.status));

      const res = await fetch(`${API}/children/${childId}/review`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ documentFeedback, overallComment }),
      });
      const j = await res.json();
      if (!res.ok) return alert(j?.error || 'Failed to save review');
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function finalizeReview() {
    if (!window.confirm('Finalize your review? You will not be able to edit afterwards.')) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/children/${childId}/review/finalize`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ finalComment: overallComment }),
      });
      const j = await res.json();
      if (!res.ok) return alert(j?.error || 'Failed to finalize review');
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function secretaryApprove() {
    if (!window.confirm('Approve this request?')) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/children/${childId}/secretary/approve`, {
        method: 'POST',
        headers,
      });
      const j = await res.json();
      if (!res.ok) return alert(j?.error || 'Failed to approve');
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function secretarySendBack() {
    const note = prompt('Optional note to researcher (leave blank if none):') || '';
    setSaving(true);
    try {
      const res = await fetch(`${API}/children/${childId}/secretary/send-back`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ note }),
      });
      const j = await res.json();
      if (!res.ok) return alert(j?.error || 'Failed to send back');
      await load();
    } finally {
      setSaving(false);
    }
  }

  if (loading || !data) {
    return (
      <div className="proposal-details-container">
        <div className="card">
          <div className="empty-state">{loading ? 'Loading…' : 'Not found'}</div>
        </div>
      </div>
    );
  }

  const { child, documents = [] } = data;

  return (
    <div className="proposal-details-container child-page">
      <div className="card">
        <div className="details-header">
          <span className={childStatusClass(child.status)}>{child.status}</span>
        </div>
        <div className="details-grid compact-top">
          <div>
            <div className="muted">Type of request</div>
            <div className="value">{child.kind}</div>
          </div>
          <div>
            <div className="muted">Prposal title</div>
            <div className="value">{child.title} </div>
          </div>
          <div>
            <div className="muted">Parent Proposal</div>
            <div className="value">
              <span
                className="plain-link"
                onClick={() => navigate(`/proposal/${child.parent_proposal_id}`)}
              >
                {child.title ? `${child.custom_id || 'Open'}` : (child.custom_id || 'Open')}
              </span>
            </div>
          </div>
        </div>

        <div className="card compact">
          <div className="section-title">Documents Detais</div>
          <table className="table-std compact child-docs">
            <thead>
              <tr>
                <th>Document</th>
                <th>Status</th>
                <th style={{ width: 90 }}>Actions</th>
                {isReviewer && <th style={{ width: 320 }}>Reviewer Decision</th>}
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.document_id}>
                  <td>
                    <span className="doc-link">{d.document_name}</span>
                  </td>
                  <td>
                    <span className={docStatusClass(d.status)}>
                      {LABEL[d.status] || d.status}
                    </span>
                  </td>
                  <td >
                    <a
                      href={`${API_ORIGIN}${d.path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn primary sm"
                    >
                      Open
                    </a>
                  </td>

                  {isReviewer && (
                    <td>
                      <div className="rev-controls">
                        <div className="rev-options">
                          {['accepted', 'resubmit', 'clarification'].map((opt) => (
                            <label key={opt} className={!reviewerCanEdit ? 'disabled' : ''}>
                              <input
                                type="radio"
                                name={`dec-${d.document_id}`}
                                value={opt}
                                disabled={!reviewerCanEdit}
                                checked={perDoc[d.document_id]?.status === opt}
                                onChange={(e) => onChangeDoc(d.document_id, 'status', e.target.value)}
                              />{' '}
                              {opt[0].toUpperCase() + opt.slice(1)}
                            </label>
                          ))}
                        </div>
                        <textarea
                          rows={2}
                          className="card input-compact"
                          placeholder="Comment (optional)"
                          disabled={!reviewerCanEdit}
                          value={perDoc[d.document_id]?.comment || ''}
                          onChange={(e) => onChangeDoc(d.document_id, 'comment', e.target.value)}
                        />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isReviewer && (
          <div className="card compact">
            <div className="section-title">Overall Comment</div>
            <textarea
              rows={4}
              className="card input-compact"
              value={overallComment}
              onChange={(e) => setOverallComment(e.target.value)}
              disabled={!reviewerCanEdit}
              placeholder={reviewerFinalized ? 'Finalized' : 'Write your overall comment'}
            />
            <div className="row-actions">
              <button className="btn" onClick={saveReview} disabled={!reviewerCanEdit || saving}>
                Save Review
              </button>
              <button
                className="btn primary"
                onClick={finalizeReview}
                disabled={reviewerFinalized || !isReviewer || saving}
              >
                Finalize Review
              </button>
            </div>
          </div>
        )}

        {isSecretary && (
          <div className="card compact">
            <div className="section-title">Secretary Actions</div>
            <div className="row-actions">
              <button className="btn sendback" onClick={secretarySendBack} disabled={!canSendBack || saving}>
                Send Back
              </button>
              <button className="btn approve" onClick={secretaryApprove} disabled={!canApprove || saving}>
                Approve
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
